import { npsColor, type NpsSummary } from '../../api/nps';

/**
 * A single NPS readout: the score (colored by band), the promoter/passive/
 * detractor split as a stacked bar, and the response count. Below the display
 * threshold it shows a "not enough responses" state instead of a misleading
 * small-sample number.
 */
export default function NpsScoreTile({
  title,
  summary,
  minResponses,
}: {
  title: string;
  summary: NpsSummary;
  minResponses: number;
}) {
  const { responses, promoters, passives, detractors, nps } = summary;
  const pct = (n: number) => (responses > 0 ? (n / responses) * 100 : 0);

  return (
    <div className="nps-tile">
      <div className="nps-tile-head">
        <span className="nps-tile-title">{title}</span>
        <span className="nps-tile-count">{responses} response{responses === 1 ? '' : 's'}</span>
      </div>

      {nps == null ? (
        <div className="nps-tile-empty">
          {responses === 0
            ? 'No responses yet'
            : `Need ${minResponses}+ responses to show a score`}
        </div>
      ) : (
        <>
          <div className="nps-tile-score" style={{ color: npsColor(nps) }}>
            {nps > 0 ? `+${nps}` : nps}
          </div>
          <div className="nps-tile-bar" role="img"
               aria-label={`${promoters} promoters, ${passives} passives, ${detractors} detractors`}>
            {promoters > 0 && (
              <span className="seg promoter" style={{ width: `${pct(promoters)}%` }} />
            )}
            {passives > 0 && (
              <span className="seg passive" style={{ width: `${pct(passives)}%` }} />
            )}
            {detractors > 0 && (
              <span className="seg detractor" style={{ width: `${pct(detractors)}%` }} />
            )}
          </div>
          <div className="nps-tile-legend">
            <span><i className="dot promoter" />Promoters {promoters}</span>
            <span><i className="dot passive" />Passives {passives}</span>
            <span><i className="dot detractor" />Detractors {detractors}</span>
          </div>
        </>
      )}
    </div>
  );
}
